import { shortMonthAndYear } from "@similarweb/pro-logic-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups/utils/keywordGroupsQueryClient";
import {
    LABELS_FOR_TABLE_TYPES,
    TABLE_TYPES_FOR_ENDPOINT,
} from "app/common/keyword-research/keyword-generator/constants";
import { EKWGeneratorTableTabs } from "app/common/keyword-research/keyword-generator/types";
import { capitalizeIntent } from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/utils";
import { swSettings } from "app/common/services/swSettings";
import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { translate } from "app/filters/i18nFilter";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import { getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { FULFILLED_STATUS } from "app/common/keyword-research/keywords-overview/keywordsOverviewPageConsts";
import type { ApiParams } from "app/common/keyword-research/keywords-overview/keywordsOverviewPageTypes";

import { ENDPOINT } from "./keywordIdeasConsts";
import type { IKeywordIdeasResponseData, ITransformedKeywordIdeasData, IVolumeTrend } from "./keywordIdeasTypes";

const fetchService = DefaultFetchService.getInstance();
const WEB_SOURCE = "webSource";

const getUrlForKeyWord = (keyword: string, country: string, duration: string) => {
    const pageState = "organicsearch_keywordAnalysis_overview_2";
    const state = proNavigator.getState(pageState);
    const configId = proNavigator.getConfigId(state);
    const defaultParams = swSettings.components[configId].defaultParams;
    return proNavigator.href(pageState, { ...defaultParams, keyword, country, duration });
};

const sortVolumeTrend = (volumeTrend: IVolumeTrend | undefined) => {
    if (!volumeTrend) {
        return;
    }
    return Object.entries(volumeTrend)
        .sort(([key1], [key2]) => key1.localeCompare(key2))
        .reduce((obj, [key, value]) => {
            obj[key] = value;
            return obj;
        }, {});
};

export const transformData =
    (duration: string, country: string, webSource: string, showChange: boolean) =>
    (data: IKeywordIdeasResponseData): ITransformedKeywordIdeasData => {
        if (!data) {
            return;
        }
        const countryName = CountryService.getCountryById(country).text;
        const durationText = DurationService.getFormattedDateOutOfDurationParam(duration, shortMonthAndYear);
        return {
            records: data.records
                ?.map((keywordGroupDetail) => {
                    if (!keywordGroupDetail.keyword) {
                        return;
                    }
                    return {
                        ...keywordGroupDetail,
                        volumeTrend: sortVolumeTrend(keywordGroupDetail.volumeTrend),
                        SearchTerm: keywordGroupDetail.keyword,
                        url: getUrlForKeyWord(keywordGroupDetail.keyword, country, duration),
                        country,
                        countryName,
                        durationText,
                        web_source: webSource,
                        intents: {
                            PrimaryIntent: capitalizeIntent(keywordGroupDetail.primaryIntent || ""),
                            SecondaryIntent: capitalizeIntent(keywordGroupDetail.secondaryIntent || ""),
                        },
                        windowVolume: getVolumeValue(keywordGroupDetail.windowVolume),
                        averageVolume: getVolumeValue(keywordGroupDetail.averageVolume),
                        monthlyVolume: getVolumeValue(keywordGroupDetail.monthlyVolume),
                        ...(showChange && {
                            VolumeChange: {
                                first: keywordGroupDetail?.volume,
                                last: keywordGroupDetail?.prevVolume,
                                change: keywordGroupDetail?.volumeChange,
                                changeState: keywordGroupDetail?.volumeChangeState,
                            },
                        }),
                    };
                })
                .filter(Boolean),
        };
    };

export const getUpdatedSubtitleFilters = (subtitleFilters: Filter[], duration: string): Filter[] => {
    const updatedSubtitleFilters = [...subtitleFilters];
    if (duration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS) {
        updatedSubtitleFilters.shift();
        updatedSubtitleFilters.unshift({
            filter: "icon",
            iconName: "daily-ranking",
            value: translate("datepicker.allTime"),
        });
    }
    const filtersWithoutWebSource = updatedSubtitleFilters.filter((filter) => filter.filter !== WEB_SOURCE);
    const webSourceFilter = {
        filter: WEB_SOURCE,
        value: devicesTypes.TOTAL,
    };
    return [...filtersWithoutWebSource, webSourceFilter];
};

export const getApiParams = async (
    isKeywordGroup: boolean,
    keys: string,
    duration: string,
    country: string,
    tabName: EKWGeneratorTableTabs,
): Promise<ApiParams> => {
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const group = await keywordGroupsQueryClient.getGroupById(keys);
    const baseParams = {
        keyword: keys,
        country,
        from,
        to,
        isWindow,
        webSource: devicesTypes.TOTAL,
        rowsPerPage: 5,
        asc: false,
        ...(isKeywordGroup && {
            GroupHash: group?.GroupHash,
        }),
    };

    const volumeSort = isWindow ? "windowVolume" : "monthlyVolume";

    switch (tabName) {
        case EKWGeneratorTableTabs.relatedKeywords:
            return {
                ...baseParams,
                sort: "score",
                type: TABLE_TYPES_FOR_ENDPOINT.related,
            };
        case EKWGeneratorTableTabs.questions:
            return {
                ...baseParams,
                sort: volumeSort,
                type: TABLE_TYPES_FOR_ENDPOINT.broad,
                label: LABELS_FOR_TABLE_TYPES.questions,
            };
        case EKWGeneratorTableTabs.trending:
            return {
                ...baseParams,
                sort: volumeSort,
                type: TABLE_TYPES_FOR_ENDPOINT.broad,
                label: LABELS_FOR_TABLE_TYPES.trending,
            };
        default:
            return baseParams;
    }
};

const fetchData = async (
    relatedApiParams: ApiParams,
    questionsApiParams: ApiParams,
    trendingApiParams: ApiParams,
): Promise<{
    relatedTableData: IKeywordIdeasResponseData;
    questionsTableData: IKeywordIdeasResponseData;
    trendingTableData: IKeywordIdeasResponseData;
}> => {
    const relatedPromise = fetchService.post<IKeywordIdeasResponseData>(ENDPOINT, relatedApiParams);
    const questionsPromise = fetchService.post<IKeywordIdeasResponseData>(ENDPOINT, questionsApiParams);
    const trendingPromise = fetchService.post<IKeywordIdeasResponseData>(ENDPOINT, trendingApiParams);

    const [relatedResponse, questionsResponse, trendingResponse] = await Promise.allSettled([
        relatedPromise,
        questionsPromise,
        trendingPromise,
    ]);

    const [relatedTableData, questionsTableData, trendingTableData] = [
        relatedResponse,
        questionsResponse,
        trendingResponse,
    ].map((response) => response.status === FULFILLED_STATUS && response.value);

    return {
        relatedTableData,
        questionsTableData,
        trendingTableData,
    };
};

const isDuplicateItem = (keyword: string, keywordList: string[]) =>
    keywordList.some((keywordListItem) => keywordListItem.toLocaleLowerCase() === keyword.toLocaleLowerCase());

export const validateNewKeywords = (
    currentList: string[],
    newList: string[],
): { isValid: boolean; message: string; duplicates: string[] } => {
    const maxGroupCount = KeywordGroupEditorHelpers.getMaxGroupCount();
    let message;
    const isListFull = currentList.length >= maxGroupCount;
    if (isListFull) {
        message = translate("KeywordAnalysis.keywordgroup.wizard.error.list.is.full");
    }
    const duplicates = newList.reduce((acc, keyword) => {
        if (isDuplicateItem(keyword, currentList)) {
            return [...acc, keyword];
        }
        return acc;
    }, []);

    if (duplicates.length) {
        message = translate("KeywordAnalysis.keywordgroup.wizard.error.already.exists");
    }

    return { isValid: !isListFull && duplicates?.length < newList.length, message, duplicates };
};
